import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Modal } from "@/components/ui-mm/Modal";
import { useData } from "@/context/DataContext";
import { useConfirm } from "@/context/ConfirmContext";
import { t } from "@/lib/i18n";
import { EMO, PALC, DEFAULT_CATS, UNNEC } from "@/data/constants";

export default function CategoryEditor({ open, onClose }) {
  const { db, saveSettings } = useData();
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [cur, setCur] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { if (open) { setList(db.settings.categories.map((c) => ({ ...c }))); setErr(""); setCur(null); } }, [open, db.settings.categories]);

  const upd = (i, k, v) => setList(list.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const add = () => { setList([...list, { id: `cat_${Date.now().toString(36)}`, e: "📦", n: "New", c: PALC[list.length % PALC.length], u: false, fresh: true }]); setCur(list.length); };

  const save = async () => {
    const clean = list.map(({ fresh, ...c }) => ({ ...c, n: c.n.trim(), e: c.e.trim() || "🧾" })).filter((c) => c.n);
    if (!clean.length) return setErr("You need at least one category.");
    const keep = new Set(clean.map((c) => c.id));
    const gone = { ...db.settings.gone };
    db.settings.categories.forEach((c) => { if (!keep.has(c.id)) gone[c.id] = c; });
    clean.forEach((c) => delete gone[c.id]);
    await saveSettings({ categories: clean, gone });
    onClose();
  };

  const reset = async () => {
    if (!(await confirm("Go back to the original categories? Your expenses are kept. Press Save to apply."))) return;
    setList(DEFAULT_CATS.map(([e, n, c]) => ({ id: n, e, n, c, u: UNNEC.includes(n) })));
  };

  return (
    <Modal open={open} onClose={onClose} title={t("Edit categories")} desc="Rename, recolour, add or delete. Deleted categories stay in History." testId="category-editor-dialog" wide>
      <div className="space-y-2">
        {list.map((c, i) => (
          <div key={c.id} onFocus={() => setCur(i)} className={`grid grid-cols-[48px_1fr_44px_40px_36px] items-center gap-2 rounded-[var(--ri)] border p-2 sm:grid-cols-[52px_1fr_44px_44px_40px] ${cur === i ? "border-[var(--ac)]" : "border-[var(--bd)]"}`} data-testid={`category-row-${i}`}>
            <input className="mm-input !px-1 text-center text-xl" value={c.e} maxLength={8} onChange={(e) => upd(i, "e", e.target.value)} aria-label="Emoji" />
            <input className="mm-input" value={c.n} maxLength={18} onChange={(e) => upd(i, "n", e.target.value)} aria-label="Name" data-testid={`category-name-input-${i}`} />
            <input type="color" className="h-10 w-11 cursor-pointer rounded-lg border border-[var(--bd)] bg-transparent" value={c.c} onChange={(e) => upd(i, "c", e.target.value)} aria-label="Colour" />
            <button type="button" className="grid h-10 w-10 place-items-center rounded-full border border-[var(--bd)]" onClick={() => upd(i, "u", !c.u)} title={c.u ? "Unnecessary" : "Necessary"} aria-label={c.u ? "Unnecessary" : "Necessary"} data-testid={`category-necessity-toggle-${i}`} data-state={c.u ? "unnecessary" : "necessary"}>
              <span className="h-5 w-5 rounded-full transition-colors" style={{ background: c.u ? "var(--unn)" : "var(--nec)" }} />
            </button>
            <button type="button" className="grid h-9 w-9 place-items-center rounded-full text-[var(--dg)] hover:bg-[var(--bg)]" onClick={() => setList(list.filter((_, j) => j !== i))} aria-label="Delete category" data-testid={`category-delete-button-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <button type="button" className="mm-btn ghost sm mt-3" onClick={add} data-testid="category-add-button"><Plus className="h-4 w-4" /> {t("Add category")}</button>
      {cur !== null && list[cur] && (
        <div className="mt-4">
          <div className="mb-2 text-xs text-[var(--mu)]">Tap an emoji for “{list[cur].n}”</div>
          <div className="flex flex-wrap gap-1">{EMO.map((e) => <button type="button" key={e} className="h-9 w-9 rounded-lg text-xl hover:bg-[var(--bg)]" onClick={() => upd(cur, "e", e)}>{e}</button>)}</div>
        </div>
      )}
      <p className="mt-4 flex items-center gap-3 rounded-[var(--ri)] bg-[var(--bg)] p-3 text-sm" data-testid="category-necessity-note">
        <span className="flex gap-1"><i className="h-3.5 w-3.5 rounded-full" style={{ background: "var(--nec)" }} /><i className="h-3.5 w-3.5 rounded-full" style={{ background: "var(--unn)" }} /></span>
        {t("Blue = necessary, Red = unnecessary. Tap the dot to switch.")}
      </p>
      {err && <p className="mt-3 text-sm text-[var(--dg)]">{err}</p>}
      <div className="mt-6 flex flex-wrap justify-between gap-2">
        <button type="button" className="mm-btn ghost sm" onClick={reset} data-testid="category-reset-button">{t("↺ Reset to original")}</button>
        <div className="flex gap-2">
          <button type="button" className="mm-btn ghost" onClick={onClose}>{t("Cancel")}</button>
          <button type="button" className="mm-btn" onClick={save} data-testid="category-save-button">{t("Save")}</button>
        </div>
      </div>
    </Modal>
  );
}
